import type { DocumentType } from '../../types/tuition-assistance.ts';

/**
 * Shared asset↔bucket matching for the categorized-upload feature.
 *
 * The upload rows and the guided wizard's submission gate must read the SAME
 * bucket membership: when they diverged, the gate blocked submission on assets
 * no row rendered, with nothing actionable on screen (tuitionio/tio-front-end#9760).
 */

/**
 * The subset of TasAsset the matching reads. Structural so the wizard can pass
 * live records and unit tests can pass plain objects.
 */
export interface CategorizedUploadAssetLike {
  attachmentDeletedAt: string | null;
  classification: string | null;
  uploadRowKey: string | null;
  employeeSelectedType: DocumentType | null;
}

/** One admin-configured attachment row (from `documentEntriesFor`). */
export interface DocumentEntryLike {
  label: string;
  type: DocumentType;
}

/** Live = still attached (not discarded) and filed under this step's classification. */
export function isLiveForClassification(
  asset: CategorizedUploadAssetLike,
  classification: string
): boolean {
  return asset.attachmentDeletedAt === null && asset.classification === classification;
}

/**
 * Row-display membership. An asset tagged with an upload row key belongs only
 * to the row it was filed under; untagged (legacy) assets match by declared
 * type. Callers filter for liveness separately (the discarded tray needs
 * non-live assets).
 */
export function assetMatchesDocumentEntry(
  asset: CategorizedUploadAssetLike,
  entry: DocumentEntryLike
): boolean {
  if (asset.uploadRowKey) return asset.uploadRowKey === entry.label;
  return asset.employeeSelectedType === entry.type;
}

/**
 * True when a live upload under this classification carries neither a row key
 * nor a declared type — i.e. it predates the template's categorized rows (or
 * came from the legacy free-form uploader) and therefore can never satisfy a
 * configured entry.
 *
 * Callers on long-lived steps use this as a carryover escape hatch: an
 * application can sit in `ATTEND` for months, so a template that grows document
 * rows mid-flight must not strand evidence the employee already uploaded and
 * cannot re-file (the #9760 failure mode — a gate with nothing actionable on
 * screen).
 */
export function hasUntaggedLegacyUpload(
  assets: readonly CategorizedUploadAssetLike[],
  classification: string
): boolean {
  return assets.some(
    (asset) =>
      isLiveForClassification(asset, classification) &&
      !asset.uploadRowKey &&
      !asset.employeeSelectedType
  );
}

/**
 * The collection form of `hasUntaggedLegacyUpload`, for surfaces that need to
 * SHOW the carryover population rather than just gate on it: live uploads
 * under this classification that no categorized row can place (tio-front-end
 * #10226 — legacy-surface uploads were invisible on categorized surfaces).
 */
export function untaggedLegacyUploads<T extends CategorizedUploadAssetLike>(
  assets: readonly T[],
  classification: string
): T[] {
  return assets.filter(
    (asset) =>
      isLiveForClassification(asset, classification) &&
      !asset.uploadRowKey &&
      !asset.employeeSelectedType
  );
}

/**
 * Submission-gate presence check: every configured document type has at least
 * one live upload. Each asset counts toward exactly one type: a row key that
 * still matches a configured row pins it to that row's type (the row the
 * employee sees it in), and only an unrecognized/absent row key falls back to
 * the declared type — so a visible file always counts where it's displayed,
 * a file no row renders (renamed row, cross-surface upload) can only satisfy
 * the gate rather than block it, and one upload can never double-count for
 * two types.
 *
 * AI validation verdicts are intentionally not consulted: they are advisory
 * for reviewers and must never gate submission. Verdict tags can be orphaned
 * at `pending` when no browser session sees the AI response (tio-api#7874),
 * which hard-blocked employees when the gate keyed off them (#9760).
 *
 * Vacuously true for an empty entry list — callers decide whether an
 * unconfigured step falls back to a legacy presence check.
 */
export function hasRequiredCategorizedUploads(
  assets: readonly CategorizedUploadAssetLike[],
  classification: string,
  entries: readonly DocumentEntryLike[]
): boolean {
  const typeByLabel = new Map(entries.map((entry) => [entry.label, entry.type]));
  const satisfiedTypes = new Set<DocumentType>();

  for (const asset of assets) {
    if (!isLiveForClassification(asset, classification)) continue;
    const rowType = asset.uploadRowKey ? typeByLabel.get(asset.uploadRowKey) : undefined;
    const countsToward = rowType ?? asset.employeeSelectedType;
    if (countsToward) satisfiedTypes.add(countsToward);
  }

  return entries.every((entry) => satisfiedTypes.has(entry.type));
}
